import { DEFAULT_AVAILABILITY_ORDER_SUGGESTION } from '@apps/main/modules/recipe/ingredients/hotelIngredient';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients/hotelIngredient';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import { isHotelIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  HotelConstraintQL,
  HotelConstraintSelectionQL,
  HotelIngredientAdapter,
} from '@libs/trip-planner-interfaces';
import {
  AccommodationRule,
  ExperienceFilter,
  IngredientType,
  AmenitiesFilter,
} from '@libs/trip-planner-interfaces';
import {
  getHotelConstraintSelectionQL,
  toSelectedHotelType,
  toSelectedInputBoardBasisQL,
} from '@libs/search-service-smart-search';
import {
  FullTextConstraint,
  PolygonIdConstraint,
} from '@libs/constraint-builder';

import { getBaseConstraint } from '../baseIngredientAdapter';

export function adaptHotelIngredientToDestinationAvailabilitySearchCriteria(
  stayIngredient: HotelIngredient | OwnStayIngredient,
  isSearchingActivities: boolean
): HotelIngredientAdapter {
  const searchType = isSearchingActivities
    ? IngredientType.Activity
    : IngredientType.Hotel;

  const constraint: HotelConstraintQL = {
    ...getDestinationIngredientConstraintSelection(stayIngredient),
    maxNumberOfHotels: 200,
    boardBasis: [],
    selectedHotelTypes: [],
  };

  if (
    searchType === IngredientType.Hotel &&
    isHotelIngredient(stayIngredient)
  ) {
    constraint.starRating = stayIngredient.filters.starRating;

    constraint.hasPool =
      stayIngredient.filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Pool
      );
    constraint.hasParking =
      stayIngredient.filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Parking
      );
    constraint.hasSpa =
      stayIngredient.filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Spa
      );
    constraint.hasGym =
      stayIngredient.filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Gym
      );
    constraint.hasInternet =
      stayIngredient.filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Wifi
      );
    constraint.hasElectricVehicleChargingStation =
      stayIngredient.filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.VehicleChargingStation
      );
    constraint.hasAirConditioning =
      stayIngredient.filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.AirConditioned
      );
    constraint.isBusinessProperty =
      stayIngredient.filters.selectedExperienceFilters.includes(
        ExperienceFilter.Business
      );
    constraint.isFamilyProperty =
      stayIngredient.filters.selectedExperienceFilters.includes(
        ExperienceFilter.Family
      );
    constraint.petsAreAllowed =
      stayIngredient.filters.selectedExperienceFilters.includes(
        ExperienceFilter.PetsAllowed
      );
    constraint.isGreenSustainableProperty =
      stayIngredient.filters.selectedExperienceFilters.includes(
        ExperienceFilter.GreenSustainableProperty
      );

    constraint.freeCancellation =
      stayIngredient.filters.selectedHotelRules.includes(
        AccommodationRule.FreeCancellation
      );

    constraint.boardBasis = stayIngredient.filters.boardBasis.map(
      toSelectedInputBoardBasisQL
    );

    constraint.polygonIds = stayIngredient.filters.polygonIds;

    constraint.selectedHotelTypes = stayIngredient
      .getSelectedAccommodationTypes()
      .map(toSelectedHotelType);

    if (stayIngredient.filters.filterText) {
      constraint.hotelName = stayIngredient.filters.filterText;
    }
  } else {
    if (stayIngredient.filters.activitiesFilterText) {
      constraint.hotelName = stayIngredient.filters.activitiesFilterText;
    }

    constraint.selectedHotelTypes = stayIngredient
      .getSelectedActivityTypes()
      .map(toSelectedHotelType);
  }

  return {
    ingredientType: IngredientType.Hotel,
    ingredientId: stayIngredient.ingredientId,
    numberOfNights: stayIngredient.numberOfNights,
    constraint,
    heartedHotels: [],
    subIngredients: [],
    suggestedBy: isHotelIngredient(stayIngredient)
      ? stayIngredient.availabilityOrder
      : DEFAULT_AVAILABILITY_ORDER_SUGGESTION,
  };
}

export function getDestinationIngredientConstraintSelection(
  stayIngredient: HotelIngredient | OwnStayIngredient
): HotelConstraintSelectionQL {
  const polgyonIds = isHotelIngredient(stayIngredient)
    ? stayIngredient.filters.polygonIds
    : undefined;
  const constraint = getBaseConstraint(stayIngredient.searchTerm, polgyonIds);
  if (stayIngredient.polygonConstraint) {
    constraint.selection = stayIngredient.polygonConstraint;
  } else if (
    isHotelIngredient(stayIngredient) &&
    stayIngredient.nearbyPolygon
  ) {
    const { id, name } = stayIngredient.nearbyPolygon;

    constraint.selection = id
      ? new PolygonIdConstraint(id)
      : name
        ? new FullTextConstraint(name)
        : constraint.selection;
  }

  constraint.maxNumberOfHotels = 200;

  return getHotelConstraintSelectionQL(constraint);
}
